import { IPromise, IQService } from "angular";
import { IAssigneesStoreState, assigneeAvatar, deletedAssignee } from "@gtmhub/assignees";
import { GoalService } from "@gtmhub/goals/goal.service";
import { IGoal } from "@gtmhub/goals/models";
import { localize } from "@gtmhub/localization";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { IRole, RoleService } from "@gtmhub/roles";
import { ISessionsStoreState } from "@gtmhub/sessions";
import { INgRedux } from "@gtmhub/state-management";
import { getCurrentUserId } from "@gtmhub/users";
import { Search } from "@webapp/search/models/search.models";
import { IArrayGridFilter, IArrayGridFilterDefinition, IAssigneeTagOptions, IDynamicValue, IGridFilterTag, entryDisplayName, entryValue } from "./models";

export class GridFiltersTagFactory {
  private roleTagValuesPromise: IPromise<IGridFilterTag[]>;

  static $inject = ["$q", "$ngRedux", "MetricService", "GoalService", "RoleService"];

  constructor(
    private $q: IQService,
    private $ngRedux: INgRedux,
    private metricService: MetricService,
    private goalService: GoalService,
    private roleService: RoleService
  ) {}

  private get assignees() {
    return this.$ngRedux.getState<IAssigneesStoreState>().assignees.map;
  }

  private get sessions() {
    return this.$ngRedux.getState<ISessionsStoreState>().sessions.map;
  }

  createTags(definition: IArrayGridFilterDefinition, filter: IArrayGridFilter): IPromise<IGridFilterTag[]> {
    switch (definition.arrayType) {
      case "tag":
        return this.$q.resolve((filter.value || []).map<IGridFilterTag>((tag) => this.createSimpleTag(tag)));

      case "assignee":
      case "user":
      case "team":
        return this.$q.resolve(this.createTagsFromAssigneeFilter(filter, { includeSubteamsAndMembers: definition.arrayType === "assignee" }));

      case "role":
        return this.createTagsFromRoleFilter(filter);

      case "goal":
        return this.createTagsFromGoals(filter);

      case "okr":
        return this.createTagsFromOkr(filter);

      case "session":
        return this.$q.resolve(this.createTagsFromSessionFilter(filter));

      case "entity":
        return this.$q.resolve((filter.entityData || []).map<IGridFilterTag>((entity) => ({ id: entity.value.toString(), name: entity.displayName })));

      case "static": {
        const entries = definition.entries.reduce((entries, entry) => Object.assign(entries, { [entryValue(entry).toString()]: entryDisplayName(entry) }), {});

        return this.$q.resolve((filter.value || []).map<IGridFilterTag>((value) => ({ id: value, name: entries[value] || value })));
      }
    }
  }

  createSessionTag(sessionId: string): IGridFilterTag {
    const session = this.sessions[sessionId];
    const id = sessionId;
    return session
      ? {
          id,
          name: session.title,
          avatar: {
            color: session.color,
          },
        }
      : this.missingTag(id, "missing_session");
  }

  createAssigneeTag(assigneeId: string): IGridFilterTag {
    const assignee = this.assignees[assigneeId];
    if (!assignee) {
      return { id: assigneeId, name: "", avatar: assigneeAvatar(deletedAssignee(assigneeId)) };
    }
    return { id: assignee.id, name: assignee.name, avatar: assigneeAvatar(assignee), isActive: assignee.isActive };
  }

  createGoalTag(goal: IGoal): IGridFilterTag {
    return {
      id: goal.id,
      name: goal.name,
      avatar: { picture: require("wwwroot/img/icons/objective.svg") },
    };
  }

  createMetricTag(metric: IMetric): IGridFilterTag {
    return {
      id: metric.id,
      name: metric.name,
      avatar: { picture: require("wwwroot/img/icons/metric.svg") },
    };
  }

  createOkrTag(item: Search<"goals"> | Search<"metrics">): IGridFilterTag {
    return {
      id: item.id,
      name: item.fields.name,
      avatar: { picture: item.collectionName === "goals" ? require("wwwroot/img/icons/objective.svg") : require("wwwroot/img/icons/metric.svg") },
    };
  }

  createSimpleTag(tag: string): IGridFilterTag {
    return { id: tag, name: tag };
  }

  createRoleTag(role: IRole): IGridFilterTag {
    return {
      id: role.id,
      name: role.name,
    };
  }

  createTeamPlusMembersTag(teamId: string): IGridFilterTag {
    const assignee = this.assignees[teamId];
    const id = `teamPlusMembers-${teamId}`;
    const dynamicValue: IDynamicValue = { type: "teamPlusMembers", teamId };
    return assignee
      ? {
          id,
          name: `${assignee.name} ${localize("plus_its_members")}`,
          avatar: assigneeAvatar(assignee),
          dynamicValue,
        }
      : this.missingTag(id, "missing_owner", dynamicValue);
  }

  createTeamOrgTag(teamId: string, options?: IAssigneeTagOptions): IGridFilterTag {
    const assignee = this.assignees[teamId];
    const id = `teamOrg-${teamId}`;
    const name = localize(options?.includeSubteamsAndMembers ? "gh_team_with_subteams_and_members" : "gh_team_with_subteams", { name: assignee.name });
    const type = options?.includeSubteamsAndMembers ? "teamOrgPlusMembers" : "teamOrg";
    const dynamicValue: IDynamicValue = { type, teamId };

    return assignee
      ? {
          id,
          name,
          avatar: assigneeAvatar(assignee),
          dynamicValue,
        }
      : this.missingTag(id, "missing_owner", dynamicValue);
  }

  createMyTeamsTag(): IGridFilterTag {
    return {
      id: "myTeams",
      name: localize("my_teams"),
      avatar: { picture: require("wwwroot/img/icons/team2.svg") },
      dynamicValue: { type: "myTeams" },
    };
  }

  createMeTag(): IGridFilterTag {
    const currentUserId = getCurrentUserId();
    const assignee = this.assignees[currentUserId];
    const id = "me";
    const dynamicValue: IDynamicValue = { type: "me" };
    return assignee
      ? {
          id,
          name: `${localize("current_user_dynamic")}`,
          avatar: { picture: require("wwwroot/img/icons/unknown.svg") },
          dynamicValue,
        }
      : this.missingTag(id, "missing_owner", dynamicValue);
  }

  createActiveSessionsTag(): IGridFilterTag {
    return {
      id: "activeSessions",
      name: localize("active_sessions"),
      avatar: { picture: require("wwwroot/img/icons/session.svg") },
      dynamicValue: { type: "activeSessions" },
    };
  }

  private missingTag(id: string, nameKey: string, dynamicValue?: IDynamicValue): IGridFilterTag {
    return {
      id,
      name: localize(nameKey),
      avatar: assigneeAvatar({ avatar: "emoticons_big-eyes", color: "#0055cc" }),
      dynamicValue,
    };
  }

  private createTagsFromSessionFilter(filter: IArrayGridFilter): IGridFilterTag[] {
    const dynamicValueTags = (filter.dynamicValues || []).reduce<IGridFilterTag[]>((tags, dynamicValue) => {
      if (dynamicValue.type === "activeSessions") {
        const tag = this.createActiveSessionsTag();
        return [...tags, tag];
      }

      return tags;
    }, []);

    const sessionTags = (filter.value || []).filter((sessionId) => sessionId in this.sessions).map<IGridFilterTag>((sessionId) => this.createSessionTag(sessionId));

    return [...dynamicValueTags, ...sessionTags];
  }

  private createTagsFromOkr(filter: IArrayGridFilter): IPromise<IGridFilterTag[]> {
    return this.$q.all([this.createTagsFromGoals(filter), this.createTagsFromMetrics(filter)]).then(([goalTags, metricTags]) => [...goalTags, ...metricTags]);
  }

  private createTagsFromMetrics(filter: IArrayGridFilter): IPromise<IGridFilterTag[]> {
    const metricIds = filter.value || [];
    if (!metricIds.length) {
      return this.$q.resolve([]);
    }

    return this.metricService
      .getMetricsV2({
        fields: "id,name",
        filter: {
          _id: { $in: metricIds },
        },
      })
      .then((metricsCollection) => metricsCollection.items.map<IGridFilterTag>((metric) => this.createMetricTag(metric)));
  }

  private createTagsFromGoals(filter: IArrayGridFilter): IPromise<IGridFilterTag[]> {
    const goalIds = filter.value || [];
    if (!goalIds.length) {
      return this.$q.resolve([]);
    }

    return this.goalService
      .getGoalsV2({
        fields: "id,name",
        filter: {
          _id: { $in: goalIds },
        },
      })
      .then((goalsCollection) => goalsCollection.items.map<IGridFilterTag>((goal) => this.createGoalTag(goal)));
  }

  private createTagsFromAssigneeFilter(filter: IArrayGridFilter, options?: IAssigneeTagOptions): IGridFilterTag[] {
    const dynamicValueTags = (filter.dynamicValues || []).reduce<IGridFilterTag[]>((tags, dynamicValue) => {
      switch (dynamicValue.type) {
        case "me": {
          const tag = this.createMeTag();
          return [...tags, tag];
        }

        case "myTeams": {
          const tag = this.createMyTeamsTag();
          return [...tags, tag];
        }

        case "teamPlusMembers": {
          const tag = this.createTeamPlusMembersTag(dynamicValue.teamId);
          return [...tags, tag];
        }

        case "teamOrg":
        case "teamOrgPlusMembers": {
          const tag = this.createTeamOrgTag(dynamicValue.teamId, options);
          return [...tags, tag];
        }
      }
      return tags;
    }, []);

    const assigneeTags = (filter.value || []).map<IGridFilterTag>((assigneeId) => this.createAssigneeTag(assigneeId));

    return [...dynamicValueTags, ...assigneeTags];
  }

  private createTagsFromRoleFilter(filter: IArrayGridFilter): IPromise<IGridFilterTag[]> {
    const value = filter.value || [];
    return this.getRoles().then((tags) => tags.filter((tag) => value.includes(tag.id)));
  }

  private getRoles(): IPromise<IGridFilterTag[]> {
    if (!this.roleTagValuesPromise) {
      this.roleTagValuesPromise = this.roleService.getRoles().then((col) => col.items.map<IGridFilterTag>((role) => this.createRoleTag(role)));
    }

    return this.roleTagValuesPromise;
  }
}
